import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/staff/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/staff/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Accesso non riuscito");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] fs-dark text-white flex flex-col">
      <header className="w-full">
        <div className="mx-auto max-w-md px-6 h-20 flex items-center justify-between">
          <BrandLogo variant="light" className="h-7" />
          <button onClick={() => navigate("/")} className="text-xs text-white/60 hover:text-white transition-colors" data-testid="to-client-link">
            Area cliente
          </button>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-md px-6 flex flex-col justify-center pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="fs-eyebrow text-white/50">Area riservata</p>
          <h1 className="mt-3 font-heading text-3xl font-700 tracking-tight">Longevity Check<br />Dashboard</h1>
          <p className="mt-3 text-sm text-white/60">Accesso riservato allo staff FORMAeSALUTE.</p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="fs-eyebrow text-[10px] text-white/50">Email</Label>
              <Input
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="h-14 rounded-xl text-base bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-white/40"
                placeholder="nome@formaesalute.it"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="fs-eyebrow text-[10px] text-white/50">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="h-14 rounded-xl text-base bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-white/40"
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="mt-2 w-full h-14 rounded-full text-base font-semibold bg-white text-black hover:bg-white/90"
            >
              {loading ? "Accesso…" : (<><LogIn className="mr-2 h-5 w-5" /> Accedi</>)}
            </Button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
